import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Switch,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../components/DirectionalIcon';
import { ScreenLayout, Header } from '../components';
import { Button } from '../components/design-system';
import { RichTextEditor } from '../components/design-system/RichTextEditor';
import { MapLocationPicker } from '../components/MapLocationPicker';
import { ZoneMapPicker, ZoneResult } from '../components/ZoneMapPicker';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';

// ─── Constants ────────────────────────────────────────────────────────────────

type MethodType = 'flat_rate' | 'free' | 'local_pickup' | 'external_provider' | 'online_delivery' | 'location_based' | 'custom';

/** Structured zone saved from ZoneMapPicker — no free-text entry */
interface ZoneForm {
  name: string;
  price: string;
  mode: 'city' | 'country' | 'radius';
  // city mode
  cityName?: string;
  center?: { latitude: number; longitude: number };
  polygon?: [number, number][];
  polygons?: [number, number][][]; // all rings for MultiPolygon cities
  // radius mode (also used as fallback coverage for city zones)
  radiusKm?: number;
  // optional
  estimatedMin: string;
  estimatedMax: string;
}

const TYPE_OPTIONS: { value: MethodType; label: string; icon: string; color: string; hint: string }[] = [
  { value: 'flat_rate',         label: 'Flat Rate',         icon: 'pricetag-outline',       color: '#4A90E2', hint: 'Fixed cost regardless of order size' },
  { value: 'free',              label: 'Free',              icon: 'gift-outline',            color: '#50C878', hint: 'No shipping cost (optional min order)' },
  { value: 'location_based',    label: 'Zone-Based',        icon: 'map-outline',             color: '#E8624A', hint: 'Different price per city or delivery radius' },
  { value: 'local_pickup',      label: 'Local Pickup',      icon: 'storefront-outline',      color: '#FF9500', hint: 'Customer picks up from your location' },
  { value: 'external_provider', label: 'Carrier / Provider',icon: 'airplane-outline',        color: '#7B68EE', hint: 'DHL, FedEx, Aramex, UPS or other carrier' },
  { value: 'online_delivery',   label: 'Online Delivery',   icon: 'cloud-download-outline',  color: '#00BCD4', hint: 'Digital products delivered online' },
  { value: 'custom',            label: 'Custom',            icon: 'options-outline',         color: '#9E9E9E', hint: 'Any other delivery arrangement' },
];

const PRODUCT_TYPE_OPTIONS = [
  { value: 'physical', label: 'Physical', icon: 'cube-outline' },
  { value: 'digital',  label: 'Digital',  icon: 'cloud-outline' },
  { value: 'service',  label: 'Service',  icon: 'construct-outline' },
];

const KNOWN_PROVIDERS = ['DHL', 'FedEx', 'Aramex', 'UPS', 'USPS', 'Royal Mail', 'Custom'];

interface PickupLocation {
  latitude: number;
  longitude: number;
  address: string;
}

interface FormFields {
  name: string;
  type: MethodType;
  description: string;
  price: string;
  estimatedMin: string;
  estimatedMax: string;
  isActive: boolean;
  availableFor: string[];
  providerName: string;
  accountReference: string;
  minOrderAmount: string;
  belowMinCost: string;
  expressEnabled: boolean;
  expressExtraCost: string;
  expressEstimatedMin: string;
  expressEstimatedMax: string;
  expressLabel: string;
  pickupAddress: string;
  pickupLocation?: PickupLocation;
  pickupInstructions: string;
  deliveryInstructions: string;
  // location_based
  zones: ZoneForm[];
  fallbackPrice: string;
}

const EMPTY_ZONE: ZoneForm = {
  name: '',
  price: '0',
  mode: 'city',
  estimatedMin: '',
  estimatedMax: '',
};

const EMPTY_FORM: FormFields = {
  name: '',
  type: 'flat_rate',
  description: '',
  price: '0',
  estimatedMin: '',
  estimatedMax: '',
  isActive: true,
  availableFor: ['physical', 'digital', 'service'],
  providerName: '',
  accountReference: '',
  minOrderAmount: '',
  belowMinCost: '',
  expressEnabled: false,
  expressExtraCost: '',
  expressEstimatedMin: '',
  expressEstimatedMax: '',
  expressLabel: '',
  pickupAddress: '',
  pickupLocation: undefined,
  pickupInstructions: '',
  deliveryInstructions: '',
  zones: [],
  fallbackPrice: '',
};

// ─── Field helpers ────────────────────────────────────────────────────────────

const FieldLabel: React.FC<{ label: string; required?: boolean }> = ({ label, required }) => {
  const fStyles = useThemedStyles(getFStyles);
  return (
  <Text style={fStyles.label}>
    {label}{required ? <Text style={fStyles.required}> *</Text> : null}
  </Text>
  );
};

const Field: React.FC<{
  label: string;
  required?: boolean;
  children: React.ReactNode;
}> = ({ label, required, children }) => {
  const fStyles = useThemedStyles(getFStyles);
  return (
  <View style={fStyles.field}>
    <FieldLabel label={label} required={required} />
    {children}
  </View>
  );
};

const StyledInput: React.FC<{
  value: string;
  onChangeText: (t: string) => void;
  placeholder?: string;
  multiline?: boolean;
  keyboardType?: any;
  autoCapitalize?: any;
}> = ({ value, onChangeText, placeholder, multiline, keyboardType, autoCapitalize }) => {
  const fStyles = useThemedStyles(getFStyles);
  return (
  <TextInput
    style={[fStyles.input, multiline && fStyles.textarea]}
    value={value}
    onChangeText={onChangeText}
    placeholder={placeholder}
    placeholderTextColor={colors.textLight}
    multiline={multiline}
    numberOfLines={multiline ? 3 : 1}
    textAlignVertical={multiline ? 'top' : 'center'}
    keyboardType={keyboardType}
    autoCapitalize={autoCapitalize}
  />
  );
};

// ─── Screen ───────────────────────────────────────────────────────────────────

interface Props {
  mode?: 'create' | 'edit';
  methodId?: string;
}

export const ShippingMethodFormScreen: React.FC<Props> = ({ mode: modeProp, methodId }) => {
  const styles = useThemedStyles(getStyles);
  const fStyles = useThemedStyles(getFStyles);
  const zStyles = useThemedStyles(getZStyles);
  const mode = modeProp ?? (methodId ? 'edit' : 'create');
  const { navigateTo, showNotification, activeTab, setActiveTab } = useAppContext();
  const [showLeftPane] = useState(Platform.OS === 'web');
  const [showRightPane] = useState(Platform.OS === 'web');

  const [fields, setFields] = useState<FormFields>(EMPTY_FORM);
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof FormFields, string>>>({});
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [zonePickerMode, setZonePickerMode] = useState<'country' | 'city' | 'radius'>('city');
  const [showZonePicker, setShowZonePicker] = useState(false);
  const [editingZoneIdx, setEditingZoneIdx] = useState<number | null>(null); // null = adding new

  // Load existing method for edit
  useEffect(() => {
    if (mode === 'edit' && methodId) {
      loadMethod();
    }
  }, [mode, methodId]);

  const loadMethod = async () => {
    try {
      setLoading(true);
      const res = await apiService.getShippingMethodById(methodId!);
      if (res.statusCode === 1000 && res.data) {
        const m = res.data;
        setFields({
          name: m.name || '',
          type: m.type || 'flat_rate',
          description: m.description || '',
          price: String(m.price ?? 0),
          estimatedMin: String(m.estimatedDays?.min ?? ''),
          estimatedMax: String(m.estimatedDays?.max ?? ''),
          isActive: m.isActive !== false,
          availableFor: m.availableFor?.length ? m.availableFor : ['physical', 'digital', 'service'],
          providerName: m.providerName || '',
          accountReference: m.accountReference || '',
          minOrderAmount: m.minOrderAmount != null ? String(m.minOrderAmount) : '',
          belowMinCost: m.belowMinCost != null ? String(m.belowMinCost) : '',
          expressEnabled: m.expressOption?.enabled || false,
          expressExtraCost: m.expressOption?.extraCost != null ? String(m.expressOption.extraCost) : '',
          expressEstimatedMin: m.expressOption?.estimatedDays?.min != null ? String(m.expressOption.estimatedDays.min) : '',
          expressEstimatedMax: m.expressOption?.estimatedDays?.max != null ? String(m.expressOption.estimatedDays.max) : '',
          expressLabel: m.expressOption?.label || '',
          pickupAddress: m.pickupAddress || '',
          pickupLocation: m.pickupLocation,
          pickupInstructions: m.pickupInstructions || '',
          deliveryInstructions: m.deliveryInstructions || '',
          zones: (m.zones || []).map((z: any): ZoneForm => {
            // Trust the persisted `mode` — only fall back to guessing from
            // shape for older zones saved before `mode` was included in the
            // save payload (see handleSave below). Every city/country zone
            // ALSO stores a covering center+radiusKm as a fallback for the
            // point-in-polygon match, so guessing from `hasRadius` alone
            // misclassifies them as pure radius zones, which then drops
            // their cities/polygon/polygons on the next save.
            const hasRadius = z.center?.latitude !== undefined && z.radiusKm !== undefined;
            const mode: ZoneForm['mode'] = z.mode || (hasRadius ? 'radius' : 'city');
            return {
              name: z.name || '',
              price: String(z.price ?? 0),
              mode,
              cityName: (z.cities || [])[0] || undefined,
              center: z.center?.latitude !== undefined
                ? { latitude: z.center.latitude, longitude: z.center.longitude }
                : undefined,
              polygon: z.polygon || undefined,
              polygons: z.polygons || undefined,
              radiusKm: z.radiusKm,
              estimatedMin: z.estimatedDays?.min != null ? String(z.estimatedDays.min) : '',
              estimatedMax: z.estimatedDays?.max != null ? String(z.estimatedDays.max) : '',
            };
          }),
          fallbackPrice: m.fallbackPrice != null ? String(m.fallbackPrice) : '',
        });
      } else {
        showNotification('error', 'Error', 'Failed to load shipping method');
        goBack();
      }
    } catch (e: any) {
      showNotification('error', 'Error', e.message || 'Failed to load shipping method');
      goBack();
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    if (Platform.OS === 'web') window.history.back();
    else navigateTo('shipping-methods');
  };

  const set = (key: keyof FormFields) => (val: any) => {
    setFields(prev => ({ ...prev, [key]: val }));
    if (errors[key]) setErrors(prev => { const e = { ...prev }; delete e[key]; return e; });
  };

  const toggleAvailableFor = (t: string) => {
    setFields(prev => {
      const cur = prev.availableFor;
      return {
        ...prev,
        availableFor: cur.includes(t) ? cur.filter(v => v !== t) : [...cur, t],
      };
    });
  };

  const validate = (): boolean => {
    const e: typeof errors = {};
    if (!fields.name.trim()) e.name = 'Name is required';
    if (fields.type === 'external_provider' && !fields.providerName.trim()) e.providerName = 'Provider name is required';
    if (fields.type === 'local_pickup' && !fields.pickupAddress.trim()) e.pickupAddress = 'Pickup address is required';
    if (fields.type === 'location_based' && fields.zones.length === 0) e.name = 'Add at least one delivery zone';
    if (fields.type !== 'free' && fields.type !== 'location_based') {
      const p = parseFloat(fields.price);
      if (isNaN(p) || p < 0) e.price = 'Enter a valid price (0 or more)';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    try {
      setSaving(true);
      const price = fields.type === 'free' ? 0 : parseFloat(fields.price) || 0;
      const estimatedDays =
        fields.estimatedMin && fields.estimatedMax
          ? { min: parseInt(fields.estimatedMin), max: parseInt(fields.estimatedMax) }
          : undefined;

      const expressEstimatedDays =
        fields.expressEstimatedMin && fields.expressEstimatedMax
          ? { min: parseInt(fields.expressEstimatedMin), max: parseInt(fields.expressEstimatedMax) }
          : undefined;

      // Build zones payload for location_based
      const zonesPayload = fields.type === 'location_based'
        ? fields.zones.map(z => ({
            name: z.name.trim(),
            price: parseFloat(z.price) || 0,
            mode: z.mode,
            cities: (z.mode === 'city' || z.mode === 'country') && z.cityName ? [z.cityName] : undefined,
            polygon: (z.mode === 'city' || z.mode === 'country') && z.polygon?.length ? z.polygon : undefined,
            polygons: (z.mode === 'city' || z.mode === 'country') && z.polygons?.length ? z.polygons : undefined,
            center: z.center,
            radiusKm: z.radiusKm,
            estimatedDays: z.estimatedMin && z.estimatedMax
              ? { min: parseInt(z.estimatedMin), max: parseInt(z.estimatedMax) }
              : undefined,
          }))
        : undefined;

      const payload = {
        name: fields.name.trim(),
        type: fields.type,
        description: fields.description.trim() || undefined,
        price,
        estimatedDays,
        isActive: fields.isActive,
        availableFor: fields.availableFor.length ? fields.availableFor : ['physical', 'digital', 'service'],
        providerName: fields.providerName.trim() || undefined,
        accountReference: fields.accountReference.trim() || undefined,
        minOrderAmount: fields.minOrderAmount ? parseFloat(fields.minOrderAmount) : undefined,
        belowMinCost: fields.belowMinCost ? parseFloat(fields.belowMinCost) : undefined,
        expressOption: fields.expressEnabled ? {
          enabled: true,
          extraCost: parseFloat(fields.expressExtraCost) || 0,
          estimatedDays: expressEstimatedDays,
          label: fields.expressLabel.trim() || undefined,
        } : { enabled: false, extraCost: 0 },
        pickupAddress: fields.pickupAddress.trim() || undefined,
        pickupLocation: fields.pickupLocation,
        pickupInstructions: fields.pickupInstructions || undefined,
        deliveryInstructions: fields.deliveryInstructions.trim() || undefined,
        zones: zonesPayload,
        fallbackPrice: fields.type === 'location_based' && fields.fallbackPrice !== ''
          ? parseFloat(fields.fallbackPrice)
          : undefined,
      };

      const res = mode === 'edit' && methodId
        ? await apiService.updateShippingMethod(methodId, payload)
        : await apiService.createShippingMethod(payload);

      if (res.statusCode === 1000) {
        showNotification('success', 'Saved', mode === 'edit' ? 'Shipping method updated' : 'Shipping method created');
        setTimeout(goBack, 1200);
      } else {
        showNotification('error', 'Error', res.errors?.[0]?.message || 'Failed to save');
      }
    } catch (e: any) {
      showNotification('error', 'Error', e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  if (loading) {
    return (
      <ScreenLayout
        header={<Header showNotifications showMessages onNotificationPress={() => navigateTo('notifications')} onMessagePress={() => navigateTo('chat')} showNavigation={Platform.OS === 'web'} activeTab={activeTab} onTabPress={handleTabPress} />}
        showBottomTabs={Platform.OS !== 'web'}
        showLeftPane={showLeftPane} onLeftPaneClose={() => {}} leftPaneContent="settings"
        onSettingsItemPress={async (itemId) => { const { setPendingSettingsItem } = await import('../utils/tempStorage'); await setPendingSettingsItem(itemId); navigateTo('settings'); }}
        showRightPane={showRightPane} onRightPaneClose={() => {}}
      >
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </ScreenLayout>
    );
  }

  const selectedTypeConfig = TYPE_OPTIONS.find(t => t.value === fields.type);

  return (
    <ScreenLayout
      header={<Header showNotifications showMessages onNotificationPress={() => navigateTo('notifications')} onMessagePress={() => navigateTo('chat')} showNavigation={Platform.OS === 'web'} activeTab={activeTab} onTabPress={handleTabPress} />}
      showBottomTabs={Platform.OS !== 'web'}
      showLeftPane={showLeftPane} onLeftPaneClose={() => {}} leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => { const { setPendingSettingsItem } = await import('../utils/tempStorage'); await setPendingSettingsItem(itemId); navigateTo('settings'); }}
      showRightPane={showRightPane} onRightPaneClose={() => {}}
    >
      {/* Page header */}
      <View style={styles.pageHeader}>
        <TouchableOpacity onPress={goBack} style={styles.backBtn} activeOpacity={0.7}>
          <DirectionalIcon name="arrow-back" size={22} color={colors.textDark} />
        </TouchableOpacity>
        <Text style={styles.pageTitle}>
          {mode === 'edit' ? 'Edit Shipping Method' : 'New Shipping Method'}
        </Text>
        <TouchableOpacity
          style={[styles.saveBtn, saving && styles.saveBtnDisabled]}
          onPress={handleSave}
          disabled={saving}
          activeOpacity={0.8}
        >
          {saving
            ? <ActivityIndicator size="small" color={colors.white} />
            : <Ionicons name="checkmark" size={22} color={colors.white} />}
        </TouchableOpacity>
      </View>

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[styles.content, Platform.OS !== 'web' && styles.contentMobile]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {/* ── Type picker ─────────────────────────────────────────────── */}
        <View style={styles.card}>
          <Text style={styles.sectionTitle}>Delivery Type</Text>
          <View style={styles.typeGrid}>
            {TYPE_OPTIONS.map(opt => {
              const active = fields.type === opt.value;
              return (
                <TouchableOpacity
                  key={opt.value}
                  style={[styles.typeOption, active && { borderColor: opt.color, backgroundColor: opt.color + '12' }]}
                  onPress={() => set('type')(opt.value)}
                  activeOpacity={0.7}
                >
                  <Ionicons name={opt.icon as any} size={22} color={active ? opt.color : colors.textLight} />
                  <Text style={[styles.typeOptionLabel, active && { color: opt.color }]}>{opt.label}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
          {selectedTypeConfig && (
            <Text style={styles.typeHint}>{selectedTypeConfig.hint}</Text>
          )}
        </View>

        {/* ── Basic info ──────────────────────────────────────────────── */}
        <View style={styles.card}>
          <Text style={styles.sectionTitle}>Basic Info</Text>

          <Field label="Name" required>
            <StyledInput
              value={fields.name}
              onChangeText={set('name')}
              placeholder="e.g. Standard Shipping, DHL Express"
            />
            {errors.name ? <Text style={fStyles.error}>{errors.name}</Text> : null}
          </Field>

          <Field label="Description">
            <StyledInput
              value={fields.description}
              onChangeText={set('description')}
              placeholder="Optional note for customers or your team"
              multiline
            />
          </Field>

          {/* Price — hidden for free/location_based types */}
          {fields.type !== 'free' && fields.type !== 'location_based' && (
            <Field label="Price (to customer)" required>
              <StyledInput
                value={fields.price}
                onChangeText={set('price')}
                placeholder="0.00"
                keyboardType="decimal-pad"
              />
              {errors.price ? <Text style={fStyles.error}>{errors.price}</Text> : null}
            </Field>
          )}

          {/* Estimated delivery days */}
          <Text style={fStyles.label}>Estimated Delivery (days)</Text>
          <View style={styles.row}>
            <View style={{ flex: 1 }}>
              <StyledInput
                value={fields.estimatedMin}
                onChangeText={set('estimatedMin')}
                placeholder="Min"
                keyboardType="number-pad"
              />
            </View>
            <Text style={styles.rowSep}>–</Text>
            <View style={{ flex: 1 }}>
              <StyledInput
                value={fields.estimatedMax}
                onChangeText={set('estimatedMax')}
                placeholder="Max"
                keyboardType="number-pad"
              />
            </View>
          </View>

          {/* Active toggle */}
          <View style={styles.toggleRow}>
            <View style={styles.toggleInfo}>
              <Text style={styles.toggleLabel}>Active</Text>
              <Text style={styles.toggleHint}>Only active methods appear during checkout</Text>
            </View>
            <Switch
              value={fields.isActive}
              onValueChange={set('isActive')}
              trackColor={{ false: colors.border, true: colors.primary + '60' }}
              thumbColor={fields.isActive ? colors.primary : colors.textLight}
            />
          </View>
        </View>

        {/* ── Available for ───────────────────────────────────────────── */}
        <View style={styles.card}>
          <Text style={styles.sectionTitle}>Available For</Text>
          <Text style={styles.sectionHint}>Which product types can use this method</Text>
          <View style={styles.chipRow}>
            {PRODUCT_TYPE_OPTIONS.map(opt => {
              const active = fields.availableFor.includes(opt.value);
              return (
                <TouchableOpacity
                  key={opt.value}
                  style={[styles.chip, active && styles.chipActive]}
                  onPress={() => toggleAvailableFor(opt.value)}
                  activeOpacity={0.7}
                >
                  <Ionicons name={opt.icon as any} size={14} color={active ? colors.white : colors.textLight} style={{ marginRight: 4 }} />
                  <Text style={[styles.chipText, active && styles.chipTextActive]}>{opt.label}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>

        {/* ── Type-specific fields ────────────────────────────────────── */}

        {/* External provider */}
        {fields.type === 'external_provider' && (
          <View style={styles.card}>
            <Text style={styles.sectionTitle}>Carrier Details</Text>

            <Text style={fStyles.label}>Provider <Text style={fStyles.required}>*</Text></Text>
            <View style={styles.providerChips}>
              {KNOWN_PROVIDERS.map(p => {
                const active = fields.providerName === p;
                return (
                  <TouchableOpacity
                    key={p}
                    style={[styles.chip, active && styles.chipActive]}
                    onPress={() => set('providerName')(p === 'Custom' ? '' : p)}
                    activeOpacity={0.7}
                  >
                    <Text style={[styles.chipText, active && styles.chipTextActive]}>{p}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
            <StyledInput
              value={fields.providerName}
              onChangeText={set('providerName')}
              placeholder="Provider name (if not listed above)"
              autoCapitalize="words"
            />
            {errors.providerName ? <Text style={fStyles.error}>{errors.providerName}</Text> : null}

            <Field label="Account / Contract Reference">
              <StyledInput
                value={fields.accountReference}
                onChangeText={set('accountReference')}
                placeholder="Optional — for your reference only"
                autoCapitalize="none"
              />
            </Field>
          </View>
        )}

        {/* Free shipping */}
        {fields.type === 'free' && (
          <View style={styles.card}>
            <Text style={styles.sectionTitle}>Free Shipping Conditions</Text>
            <Text style={styles.sectionHint}>
              Set a minimum order amount to qualify for free shipping. Orders below that amount will be charged the fallback cost instead.
            </Text>

            <Field label="Minimum Order Amount">
              <StyledInput
                value={fields.minOrderAmount}
                onChangeText={set('minOrderAmount')}
                placeholder="e.g. 50 — leave blank for always free"
                keyboardType="decimal-pad"
              />
            </Field>

            {!!fields.minOrderAmount && (
              <Field label="Cost if Below Minimum">
                <StyledInput
                  value={fields.belowMinCost}
                  onChangeText={set('belowMinCost')}
                  placeholder="e.g. 5.00 — charged when order is below minimum"
                  keyboardType="decimal-pad"
                />
              </Field>
            )}
          </View>
        )}

        {/* ── Location-based zones ────────────────────────────────────── */}
        {fields.type === 'location_based' && (
          <View style={styles.card}>
            <Text style={styles.sectionTitle}>Delivery Zones</Text>
            <Text style={styles.sectionHint}>
              Zones are evaluated top-to-bottom — first match wins. Tap to define each zone on the map.
            </Text>

            {/* Zone cards */}
            {fields.zones.map((zone, idx) => (
              <View key={idx} style={zStyles.zoneCard}>
                <View style={zStyles.zoneHeader}>
                  {/* Mode icon */}
                  <View style={[zStyles.zoneModeIcon, { backgroundColor: (zone.mode === 'city' || zone.mode === 'country') ? '#4A90E212' : '#E8624A12' }]}>
                    <Ionicons
                      name={zone.mode === 'country' ? 'earth-outline' : zone.mode === 'city' ? 'business-outline' : 'radio-button-on-outline'}
                      size={14}
                      color={(zone.mode === 'city' || zone.mode === 'country') ? '#4A90E2' : '#E8624A'}
                    />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={zStyles.zoneName}>{zone.name || `Zone ${idx + 1}`}</Text>
                    <Text style={zStyles.zoneDetail}>
                      {(zone.mode === 'city' || zone.mode === 'country')
                        ? (zone.cityName || (zone.mode === 'country' ? 'Country zone' : 'City zone'))
                        : `${zone.radiusKm ?? '?'} km radius`}
                      {zone.price ? ` · ${zone.price}` : ''}
                    </Text>
                  </View>
                  {/* Reorder handles */}
                  <View style={zStyles.zoneActions}>
                    {idx > 0 && (
                      <TouchableOpacity
                        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                        onPress={() => setFields(prev => {
                          const zones = [...prev.zones];
                          [zones[idx - 1], zones[idx]] = [zones[idx], zones[idx - 1]];
                          return { ...prev, zones };
                        })}
                      >
                        <Ionicons name="chevron-up" size={16} color={colors.textLight} />
                      </TouchableOpacity>
                    )}
                    {idx < fields.zones.length - 1 && (
                      <TouchableOpacity
                        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                        onPress={() => setFields(prev => {
                          const zones = [...prev.zones];
                          [zones[idx], zones[idx + 1]] = [zones[idx + 1], zones[idx]];
                          return { ...prev, zones };
                        })}
                      >
                        <Ionicons name="chevron-down" size={16} color={colors.textLight} />
                      </TouchableOpacity>
                    )}
                    <TouchableOpacity
                      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                      onPress={() => { setEditingZoneIdx(idx); setZonePickerMode(zone.mode === 'country' ? 'country' : zone.mode === 'radius' ? 'radius' : 'city'); setShowZonePicker(true); }}
                    >
                      <Ionicons name="map-outline" size={16} color={colors.primary} />
                    </TouchableOpacity>
                    <TouchableOpacity
                      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                      onPress={() => setFields(prev => ({ ...prev, zones: prev.zones.filter((_, i) => i !== idx) }))}
                    >
                      <Ionicons name="trash-outline" size={16} color={colors.error} />
                    </TouchableOpacity>
                  </View>
                </View>

                {/* Estimated days inline */}
                <View style={[styles.row, { marginTop: spacing.xs }]}>
                  <Text style={[fStyles.label, { marginTop: 0, flex: 1 }]}>Est. days (optional)</Text>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                    <StyledInput
                      value={zone.estimatedMin}
                      onChangeText={v => setFields(prev => {
                        const zones = [...prev.zones];
                        zones[idx] = { ...zones[idx], estimatedMin: v };
                        return { ...prev, zones };
                      })}
                      placeholder="Min"
                      keyboardType="number-pad"
                    />
                    <Text style={styles.rowSep}>–</Text>
                    <StyledInput
                      value={zone.estimatedMax}
                      onChangeText={v => setFields(prev => {
                        const zones = [...prev.zones];
                        zones[idx] = { ...zones[idx], estimatedMax: v };
                        return { ...prev, zones };
                      })}
                      placeholder="Max"
                      keyboardType="number-pad"
                    />
                  </View>
                </View>
              </View>
            ))}

            {/* Add zone buttons */}
            <View style={zStyles.addZoneRow}>
              <TouchableOpacity
                style={[zStyles.addZoneBtn, { flex: 1 }]}
                onPress={() => { setZonePickerMode('country'); setEditingZoneIdx(null); setShowZonePicker(true); }}
                activeOpacity={0.7}
              >
                <Ionicons name="earth-outline" size={16} color="#2E7D32" />
                <Text style={[zStyles.addZoneBtnText, { color: '#2E7D32' }]}>Add Country</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[zStyles.addZoneBtn, { flex: 1 }]}
                onPress={() => { setZonePickerMode('city'); setEditingZoneIdx(null); setShowZonePicker(true); }}
                activeOpacity={0.7}
              >
                <Ionicons name="business-outline" size={16} color="#4A90E2" />
                <Text style={[zStyles.addZoneBtnText, { color: '#4A90E2' }]}>Add City</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[zStyles.addZoneBtn, zStyles.addZoneBtnRadius, { flex: 1 }]}
                onPress={() => { setZonePickerMode('radius'); setEditingZoneIdx(null); setShowZonePicker(true); }}
                activeOpacity={0.7}
              >
                <Ionicons name="radio-button-on-outline" size={16} color="#E8624A" />
                <Text style={[zStyles.addZoneBtnText, { color: '#E8624A' }]}>Add Radius</Text>
              </TouchableOpacity>
            </View>

            {/* Fallback price */}
            <View style={{ marginTop: spacing.xs }}>
              <Text style={fStyles.label}>Fallback Price (outside all zones)</Text>
              <Text style={[fStyles.label, { color: colors.textLight, fontSize: 11, marginTop: 2 }]}>
                Leave blank to hide this method when no zone matches
              </Text>
              <StyledInput
                value={fields.fallbackPrice}
                onChangeText={set('fallbackPrice')}
                placeholder="e.g. 20 — or leave blank"
                keyboardType="decimal-pad"
              />
            </View>
          </View>
        )}

        {/* Express delivery option (all types) */}
        <View style={styles.card}>
          <View style={styles.toggleRow}>
            <View style={styles.toggleInfo}>
              <Text style={styles.toggleLabel}>Express Delivery</Text>
              <Text style={styles.toggleHint}>Allow customers to opt in to faster delivery for an extra fee</Text>
            </View>
            <Switch
              value={fields.expressEnabled}
              onValueChange={set('expressEnabled')}
              trackColor={{ false: colors.border, true: colors.primary + '60' }}
              thumbColor={fields.expressEnabled ? colors.primary : colors.textLight}
            />
          </View>

          {fields.expressEnabled && (
            <View style={{ gap: spacing.sm, marginTop: spacing.sm }}>
              <Field label="Express Label">
                <StyledInput
                  value={fields.expressLabel}
                  onChangeText={set('expressLabel')}
                  placeholder='e.g. "Next Day", "Same Day"'
                  autoCapitalize="words"
                />
              </Field>

              <Field label="Extra Cost">
                <StyledInput
                  value={fields.expressExtraCost}
                  onChangeText={set('expressExtraCost')}
                  placeholder="Additional charge on top of base price"
                  keyboardType="decimal-pad"
                />
              </Field>

              <Text style={fStyles.label}>Express Estimated Delivery (days)</Text>
              <View style={styles.row}>
                <View style={{ flex: 1 }}>
                  <StyledInput
                    value={fields.expressEstimatedMin}
                    onChangeText={set('expressEstimatedMin')}
                    placeholder="Min"
                    keyboardType="number-pad"
                  />
                </View>
                <Text style={styles.rowSep}>–</Text>
                <View style={{ flex: 1 }}>
                  <StyledInput
                    value={fields.expressEstimatedMax}
                    onChangeText={set('expressEstimatedMax')}
                    placeholder="Max"
                    keyboardType="number-pad"
                  />
                </View>
              </View>
            </View>
          )}
        </View>

        {/* Local pickup */}
        {fields.type === 'local_pickup' && (
          <View style={styles.card}>
            <Text style={styles.sectionTitle}>Pickup Details</Text>

            {/* Pickup Address — location selector */}
            <Text style={fStyles.label}>Pickup Address <Text style={fStyles.required}>*</Text></Text>
            <TouchableOpacity
              style={[styles.locationBtn, errors.pickupAddress ? styles.locationBtnError : null]}
              onPress={() => setShowLocationPicker(true)}
              activeOpacity={0.7}
            >
              <Ionicons name="location-outline" size={18} color={fields.pickupLocation ? colors.primary : colors.textLight} />
              <Text style={[styles.locationBtnText, fields.pickupAddress ? styles.locationBtnTextSet : null]} numberOfLines={2}>
                {fields.pickupAddress || 'Tap to select pickup location'}
              </Text>
              <DirectionalIcon name="chevron-forward" size={16} color={colors.textLight} />
            </TouchableOpacity>
            {errors.pickupAddress ? <Text style={fStyles.error}>{errors.pickupAddress}</Text> : null}

            {/* Pickup Instructions — rich editor */}
            <View style={{ marginTop: spacing.sm }}>
              <RichTextEditor
                label="Pickup Instructions"
                value={fields.pickupInstructions}
                onChange={set('pickupInstructions')}
                placeholder="Hours, parking notes, contact info, etc."
                mode="product"
              />
            </View>
          </View>
        )}

        {/* Online delivery */}
        {fields.type === 'online_delivery' && (
          <View style={styles.card}>
            <Text style={styles.sectionTitle}>Delivery Instructions</Text>
            <RichTextEditor
              label="Instructions for customers"
              value={fields.deliveryInstructions}
              onChange={set('deliveryInstructions')}
              placeholder="e.g. You'll receive a download link via email"
              mode="product"
            />
          </View>
        )}

        {/* Actions */}
        <View style={styles.actions}>
          <Button title="Cancel" variant="outline" onPress={goBack} style={styles.cancelBtn} />
          <Button
            title={mode === 'edit' ? 'Update' : 'Create'}
            variant="primary"
            onPress={handleSave}
            loading={saving}
            style={styles.saveActionBtn}
          />
        </View>
      </ScrollView>

      <MapLocationPicker
        visible={showLocationPicker}
        onLocationSelect={(loc) => {
          setFields(prev => ({
            ...prev,
            pickupLocation: loc,
            pickupAddress: loc.address || prev.pickupAddress,
          }));
          if (errors.pickupAddress) setErrors(prev => { const e = { ...prev }; delete e.pickupAddress; return e; });
          setShowLocationPicker(false);
        }}
        onClose={() => setShowLocationPicker(false)}
        initialLocation={fields.pickupLocation}
      />

      <ZoneMapPicker
        visible={showZonePicker}
        onClose={() => { setShowZonePicker(false); setEditingZoneIdx(null); }}
        onConfirm={(result: ZoneResult) => {
          const newZone: ZoneForm = {
            name: result.name,
            price: String(result.price ?? 0),
            mode: result.mode,
            cityName: (result.mode === 'city' || result.mode === 'country') ? result.cityName : undefined,
            center: result.center,
            polygon: (result.mode === 'city' || result.mode === 'country') ? result.polygon : undefined,
            polygons: (result.mode === 'city' || result.mode === 'country') ? (result as any).polygons : undefined,
            radiusKm: (result as any).radiusKm,
            estimatedMin: '',
            estimatedMax: '',
          };
          if (editingZoneIdx !== null) {
            setFields(prev => {
              const zones = [...prev.zones];
              // Preserve estimated-days — the map picker only knows about
              // location/name/price, not this zone-specific delivery estimate.
              zones[editingZoneIdx] = { ...newZone, estimatedMin: zones[editingZoneIdx].estimatedMin, estimatedMax: zones[editingZoneIdx].estimatedMax };
              return { ...prev, zones };
            });
          } else {
            setFields(prev => ({ ...prev, zones: [...prev.zones, newZone] }));
          }
          setEditingZoneIdx(null);
          setShowZonePicker(false);
        }}
        initialData={editingZoneIdx !== null ? (fields.zones[editingZoneIdx] as any) : undefined}
        initialMode={zonePickerMode}
      />
    </ScreenLayout>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const getStyles = () => StyleSheet.create({
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  pageHeader: {
    backgroundColor: colors.background.surface,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    ...designTokens.shadows.sm,
    gap: spacing.md,
  },
  backBtn: { padding: spacing.xs },
  pageTitle: {
    flex: 1,
    fontSize: typography.fontSize.xl,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  saveBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    ...designTokens.shadows.sm,
  },
  saveBtnDisabled: { opacity: 0.6 },
  scroll: { flex: 1 },
  content: { padding: spacing.lg, gap: spacing.md },
  contentMobile: { paddingBottom: 100 },
  card: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    padding: spacing.lg,
    gap: spacing.sm,
    ...designTokens.shadows.sm,
  },
  sectionTitle: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
    marginBottom: spacing.xs,
  },
  sectionHint: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
    marginTop: -spacing.xs,
  },
  typeGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  typeOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1.5,
    borderColor: colors.border,
    gap: spacing.xs,
    minWidth: 140,
    flex: 1,
  },
  typeOptionLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.medium,
  },
  typeHint: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
    marginTop: spacing.xs,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.xs,
  },
  rowSep: {
    fontSize: typography.fontSize.lg,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
  },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: spacing.sm,
  },
  toggleInfo: { flex: 1 },
  toggleLabel: {
    fontSize: typography.fontSize.base,
    fontWeight: '500',
    color: colors.textDark,
    fontFamily: typography.fontFamily.medium,
  },
  toggleHint: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
    marginTop: 2,
  },
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  providerChips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
    marginBottom: spacing.sm,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  chipActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  chipText: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.medium,
  },
  chipTextActive: {
    color: colors.white,
  },
  actions: {
    flexDirection: 'row',
    gap: spacing.md,
    marginTop: spacing.sm,
  },
  cancelBtn: { flex: 1 },
  saveActionBtn: { flex: 2 },
  locationBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1.5,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    backgroundColor: colors.background.surface,
    gap: spacing.sm,
  },
  locationBtnError: {
    borderColor: colors.error,
  },
  locationBtnText: {
    flex: 1,
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.regular,
    color: colors.textLight,
  },
  locationBtnTextSet: {
    color: colors.textDark,
  },
});

const getFStyles = () => StyleSheet.create({
  field: { gap: 4 },
  label: {
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
    fontFamily: typography.fontFamily.medium,
    marginTop: spacing.xs,
  },
  required: { color: colors.error },
  input: {
    borderWidth: 1.5,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    fontSize: typography.fontSize.base,
    color: colors.textDark,
    fontFamily: typography.fontFamily.regular,
    backgroundColor: colors.background.surface,
  },
  textarea: {
    minHeight: 80,
    paddingTop: spacing.sm,
  },
  error: {
    fontSize: typography.fontSize.xs,
    color: colors.error,
    fontFamily: typography.fontFamily.regular,
    marginTop: 2,
  },
});

const getZStyles = () => StyleSheet.create({
  zoneCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    padding: spacing.sm,
    backgroundColor: colors.background.secondary,
    marginBottom: spacing.xs,
  },
  zoneHeader: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  zoneModeIcon: {
    width: 28,
    height: 28,
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
  },
  zoneName: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  zoneDetail: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
    marginTop: 1,
  },
  zoneActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingLeft: spacing.xs,
  },
  addZoneRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
  addZoneBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingVertical: spacing.sm,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1.5,
    borderColor: '#4A90E2',
    borderStyle: 'dashed',
  },
  addZoneBtnRadius: {
    borderColor: '#E8624A',
  },
  addZoneBtnText: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.medium,
  },
});
